import { View } from 'ol';
import type { Map } from 'ol';
import Control from 'ol/control/Control';
import MousePosition from 'ol/control/MousePosition';
import ScaleLine from 'ol/control/ScaleLine';
import { createStringXY } from 'ol/coordinate';
import type { Coordinate } from 'ol/coordinate';
import type Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import type { Geometry, LineString, Polygon } from 'ol/geom';
import type VectorLayer from 'ol/layer/Vector';
import { transform } from 'ol/proj';
import { getArea, getLength } from 'ol/sphere';

/** Reads lon/lat GeoJSON into features and geometries on the maps' EPSG:3857 views. */
export const geoJSONFormat = new GeoJSON({
  dataProjection: 'EPSG:4326',
  featureProjection: 'EPSG:3857'
});

export function transformTo3857(coordinate: Coordinate) {
  return transform(coordinate, 'EPSG:4326', 'EPSG:3857');
}

// The maps' default point (lon, lat): where they open before a farm is chosen.
export const DEFAULT_CENTER: readonly [number, number] = [150.5155619, -23.322713];

/** A view of the default point, as the maps open. */
export function createDefaultView() {
  return new View({
    center: transformTo3857([...DEFAULT_CENTER]),
    zoom: 15,
    maxZoom: 21
  });
}

export function transformTo4326(coordinate: Coordinate) {
  return transform(coordinate, 'EPSG:3857', 'EPSG:4326');
}

/** The map's layer whose `id` is `layerID`. */
export function layerById(map: Map, layerID: string) {
  return map.getLayers().getArray().find(layer => layer.get('id') === layerID) ?? null;
}

/** layerById, typed as a vector layer. */
export function vectorLayerById(map: Map, layerID: string) {
  return layerById(map, layerID) as VectorLayer<Feature<Geometry>> | null;
}

/** Adds a template element (a tool panel) to the map as a control, when it is rendered. */
export function addElementControl(map: Map, element: HTMLElement | null | undefined) {
  if (element) map.addControl(new Control({ element }));
}

/** The maps' standard controls: the pointer's lon/lat, the map's tool panels, then a scale line. */
export function addMapControls(map: Map, panels: (HTMLElement | null | undefined)[]) {
  map.addControl(new MousePosition({
    coordinateFormat: createStringXY(7),
    projection: 'EPSG:4326'
  }));
  for (const panel of panels) addElementControl(map, panel);
  map.addControl(new ScaleLine({
    units: 'metric', className: 'scale-line ol-scale-line'
  }));
}

export function formatLength(line: LineString): string {
  const length = getLength(line);
  return length > 100
    ? Math.round((length / 1000) * 100) / 100 + ' km'
    : Math.round(length * 100) / 100 + ' m';
}

// HTML: the unit carries a <sup>2</sup>.
export function formatArea(polygon: Polygon) {
  const area = getArea(polygon);
  return area > 10000
    ? Math.round((area / 1000000) * 100) / 100 + ' km<sup>2</sup>'
    : Math.round(area * 100) / 100 + ' m<sup>2</sup>';
}
